import React, {Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {BadgeCheckIcon, CheckIcon, CubeIcon, DotsHorizontalIcon, InformationCircleIcon, PrinterIcon, TagIcon, XIcon} from "@heroicons/react/outline";
import Loader from "../../components/common/loader/loader";
import RemovePopup from "../../components/common/popup/remove-popup";
import DocumentHeader from "../../module/document/components/document-header";
import DocumentBody, {DOCUMENT_BODY_IMPORT_TYPE} from "../../module/document/components/document-body";
import DocumentRequestEventsButton from "../../module/document/components/document-request-events-button";
import PriceRange from "../../components/common/card/price-range";
import {StatusList} from "../../components/common/alert/status-list";
import {AcquisitionList} from "../../components/common/alert/acquisition-list";
import {useDocumentDetail} from "../../module/document/hooks/useDocumentDetail";
import {fetchDocumentBody, fetchDocumentDetail, updateDocument} from "../../module/document/services/document-service";
import * as Endpoint from "../endpoints";
import {COMPANY} from "../../api/endpoints";
import {getPathParam} from "../../utils/converter";
import {
    DOCUMENT_HEADER_Z_INDEX_CLASS,
    DOCUMENT_SYSTEM_TASKS_PANEL_Z_INDEX_CLASS,
    resolveDocumentPermissionGroup,
    resolveDocumentTypeFromSlug,
} from "../../module/document/constants";
import {getCustomFormat, getFormattedDate} from "../../utils/timeUtils";
import ImportProgress from "../../components/partials/upload/import-progress";
import {CANCELED, CONFIRMED, DRAFT, REVIEW} from "../../constants/config";
import toast from "react-hot-toast";
import {Dialog, Menu, Popover, Transition} from "@headlessui/react";
import ConfirmPopup from "../../components/common/popup/confirm-popup";
import ConfirmTextPopup from "../../components/common/popup/confirm-text-popup";
import FormPopup from "../../components/common/popup/form-popup";
import SingleReport from "../../components/partials/report/single-report";
import RequiredPopup from "../../components/common/popup/required-popup";
import BasePermissionWrapper, {
    hasPermission
} from "../../components/partials/restricted/base-permission-wrapper";
import {
    getPermission,
    permissionGroup,
    permissionSpecific,
    permissionType,
} from "../../constants/permissions";
import SystemTasks from "../../components/partials/header/system-task";
import useBackNavigation from "../../hooks/useBackNavigation";

const buildListPath = (slug) => `${Endpoint.SALES + Endpoint.DOCUMENTS_PAGE}/${slug}`;
const buildEditPath = (slug, documentId) => `${buildListPath(slug)}/${documentId}${Endpoint.DOCUMENT_EDIT}`;

const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return null;
    }
    return parsed;
};

const clampDiscountPercent = (value) => {
    if (!Number.isFinite(value)) {
        return null;
    }
    if (value < 0) {
        return 0;
    }
    if (value > 100) {
        return 100;
    }
    return value;
};

const normalizeDiscountPercent = (value) => {
    const numeric = parseNumericValue(value);
    if (numeric === null) {
        return null;
    }
    return clampDiscountPercent(numeric);
};

const parseEditableNumber = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") {
            return null;
        }
        const normalized = trimmed.replace(",", ".");
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const formatEditableNumber = (value, fractionDigits = 2) => {
    if (!Number.isFinite(value)) {
        return "";
    }
    return value.toFixed(fractionDigits);
};

const DEFAULT_STATUS_LIST = [DRAFT, REVIEW, CONFIRMED, CANCELED];
const STATUS_ALIASES = {
    cancelled: CANCELED,
};

const normalizeStatusValue = (status) => {
    if (typeof status !== "string") {
        return "";
    }
    const trimmed = status.trim();
    if (!trimmed) {
        return "";
    }
    const normalized = trimmed.toLowerCase();
    return STATUS_ALIASES[normalized] ?? normalized;
};

const NORMALIZED_CONFIRMED_STATUS = normalizeStatusValue(CONFIRMED);
const NORMALIZED_DRAFT_STATUS = normalizeStatusValue(DRAFT);
const NORMALIZED_REVIEW_STATUS = normalizeStatusValue(REVIEW);
const NORMALIZED_CANCELED_STATUS = normalizeStatusValue(CANCELED);

const SHOW_MARGIN_STORAGE_KEY = "document-detail-show-margin";

const readShowMarginPreference = (fallbackValue) => {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
        return fallbackValue;
    }
    try {
        const storedValue = false; //window.localStorage.getItem(SHOW_MARGIN_STORAGE_KEY);
        if (storedValue === null) {
            return fallbackValue;
        }
        return storedValue === "true";
    } catch (error) {
        return fallbackValue;
    }
};

const SignatureIcon = ({className = "", ...props}) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        {...props}
    >
        <path d="M4 14.5c1.4-2 2.8-2 4.2 0s2.8 2 4.2 0 2.8-2 4.2 0 2.8 2 4.2 0" />
        <path d="M3 19h18" />
    </svg>
);

const SIGNATURE_VARIANT_STYLES = {
    success: {
        card: "border-emerald-200 bg-emerald-50 text-emerald-900",
        icon: "bg-emerald-100 text-emerald-600",
    },
    info: {
        card: "border-blue-200 bg-blue-50 text-blue-900",
        icon: "bg-blue-100 text-blue-600",
    },
    warning: {
        card: "border-amber-200 bg-amber-50 text-amber-900",
        icon: "bg-amber-100 text-amber-600",
    },
    neutral: {
        card: "border-gray-200 bg-gray-50 text-gray-600",
        icon: "bg-gray-100 text-gray-500",
    },
};

const SignatureStatusCard = ({variant = "info", icon: IconComponent = SignatureIcon, title, children}) => {
    const styles = SIGNATURE_VARIANT_STYLES[variant] ?? SIGNATURE_VARIANT_STYLES.info;
    return (
        <div className={`flex items-start gap-3 rounded-lg border px-3 py-3 shadow-sm ${styles.card}`}>
            <div className={`flex h-9 w-9 items-center justify-center rounded-full ${styles.icon}`}>
                <IconComponent className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="space-y-1 text-sm leading-5">
                {title ? (
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-75">
                        {title}
                    </p>
                ) : null}
                <div>{children}</div>
            </div>
        </div>
    );
};

const ValidationSummaryModal = ({isOpen, onClose, summary, t}) => {
    const normalizedSummary = Array.isArray(summary) ? summary : [];
    const rowsWithIssues = normalizedSummary.filter((row) => Array.isArray(row?.issues) && row.issues.length > 0);
    const hasRows = normalizedSummary.length > 0;

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                className="fixed inset-0 z-[120] overflow-y-auto"
                onClose={onClose}
            >
                <div className="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block w-full max-w-3xl transform overflow-hidden rounded-lg bg-white px-4 pb-6 pt-5 text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle sm:p-6">
                            <div className="sm:flex sm:items-start sm:justify-between">
                                <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                                    {t("app.documents.validation_summary_title", {defaultValue: "Esito verifica documento"})}
                                </Dialog.Title>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="mt-3 inline-flex h-8 items-center rounded-md border border-gray-200 px-3 text-sm font-medium text-gray-500 hover:bg-gray-100 sm:mt-0"
                                >
                                    {t("app.common.close", {defaultValue: "Chiudi"})}
                                </button>
                            </div>

                            <div className="mt-6 max-h-[60vh] space-y-4 overflow-y-auto">
                                {!hasRows ? (
                                    <p className="text-sm text-gray-600">
                                        {t("app.documents.validation_no_rows", {defaultValue: "Nessuna riga disponibile per la verifica."})}
                                    </p>
                                ) : rowsWithIssues.length === 0 ? (
                                    <p className="text-sm text-emerald-600">
                                        {t("app.documents.validation_everything_ok", {defaultValue: "Tutte le righe risultano valide."})}
                                    </p>
                                ) : rowsWithIssues.map((row) => (
                                    <div
                                        key={row.rowKey}
                                        className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                                    >
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-semibold text-gray-900">
                                                {row.label}
                                            </h3>
                                            <span className="text-xs font-medium text-rose-600">
                                                {t("app.documents.validation_row_errors", {
                                                    defaultValue: "{{count}} problemi",
                                                    count: row.issues.length,
                                                })}
                                            </span>
                                        </div>
                                        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-gray-700">
                                            {row.issues.map((issue, index) => (
                                                <li key={`issue-${row.rowKey}-${index}`}>
                                                    {issue}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
};


const DocumentDetailPage = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {document_type: routeSlug, document_id: documentId} = useParams();

    const type = useMemo(() => resolveDocumentTypeFromSlug(routeSlug), [routeSlug]);
    const slug = routeSlug;
    const documentPermissionGroup = useMemo(
        () => resolveDocumentPermissionGroup({type, slug}) ?? permissionGroup.DOCUMENT,
        [slug, type],
    );

    const {document: fetchedDocument, isLoading, companyId} = useDocumentDetail({
        type,
        documentId,
    });
    const {isExporter, companyType} = useSelector((state) => state.user ?? {});
    const {permissions, company} = useSelector((state) => state.userCompany ?? {permissions: {}, company: null});
    const canEditDocumentPermission = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionType.EDIT,
        companyType,
        true,
    );
    const canDeleteDocumentPermission = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionType.DELETE,
        companyType,
        true,
    );
    const canGenerateDocument = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionSpecific.DOCUMENT_GENERATE_DOCUMENT,
        companyType,
        true,
    );
    const canViewMarginData = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionSpecific.DOCUMENT_MARGIN_DATA,
        companyType,
        true,
    );
    const canConfirmStatusPermission = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionSpecific.DOCUMENT_CONFIRM,
        companyType,
        false,
    );
    const canManageQuantityConfirmation = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionSpecific.DOCUMENT_MANAGE_EVENT_CONFIRMATION,
        companyType,
        true,
    );

    const [isRemoveOpen, setIsRemoveOpen] = useState(false);
    const [document, setDocument] = useState(null);
    const [isStatusUpdating, setStatusUpdating] = useState(false);
    const [isAcquisitionUpdating, setAcquisitionUpdating] = useState(false);
    const [areTotalsVisible, setTotalsVisible] = useState(false);
    const [areDocumentTotalsVisible, setDocumentTotalsVisible] = useState(false);
    const headerRef = useRef(null);
    const [documentHeaderHeight, setDocumentHeaderHeight] = useState(0);
    const [isMdViewport, setIsMdViewport] = useState(false);
    const [isMobileDocumentHeaderOpen, setMobileDocumentHeaderOpen] = useState(false);
    const totalsRef = useRef(null);
    const documentBodySectionRef = useRef(null);
    const [latestImport, setLatestImport] = useState(null);
    const [importRefreshKey, setImportRefreshKey] = useState(0);
    const [isOpenConfirmPackaging, setOpenConfirmPackaging] = useState(false);
    const [openType, setOpenType] = useState("");
    const [pendingStatus, setPendingStatus] = useState(null);
    const [isStatusConfirmCheckOpen, setStatusConfirmCheckOpen] = useState(false);
    const [statusConfirmDetail, setStatusConfirmDetail] = useState(null);
    const [isConfirmQuantityOpen, setConfirmQuantityOpen] = useState(false);
    const [isOpenRequird, setOpenRequird] = useState(false);
    const [requiredDetail, setRequiredDetail] = useState([]);
    const [isOpenReportGenerate, setOpenReportGenerate] = useState(false);
    const [validationSummary, setValidationSummary] = useState([]);
    const [isValidationModalOpen, setValidationModalOpen] = useState(false);
    const [isValidatingBody, setIsValidatingBody] = useState(false);
    const [showMargin, setShowMargin] = useState(() => (canViewMarginData ? readShowMarginPreference(true) : false));
    const [selectedTotalsSection, setSelectedTotalsSection] = useState("general");
    const [totalsRefreshCount, setTotalsRefreshCount] = useState(0);
    const [isCloningPopupOpen, setIsCloningPopupOpen] = useState(false);
    const [isDiscountPopupOpen, setIsDiscountPopupOpen] = useState(false);
    const [discountMode, setDiscountMode] = useState("percent");
    const [discountPercentInput, setDiscountPercentInput] = useState("");
    const [discountValueInput, setDiscountValueInput] = useState("");
    const [isDiscountUpdating, setIsDiscountUpdating] = useState(false);
    const [requestEventsRefreshKey, setRequestEventsRefreshKey] = useState(0);
    const [externalConfirmedQuantityRequest, setExternalConfirmedQuantityRequest] = useState(null);
    const cloningPollerRef = useRef(null);
    const cloningLastStatusRef = useRef(null);
    // Document detail manages scrolling internally on desktop, so disable the outer scroll container while mounted.
    useEffect(() => {
        if (!isMdViewport) {
            return undefined;
        }
        if (typeof window === "undefined" || typeof window.document === "undefined") {
            return undefined;
        }
        const scrollableElement = window.document.getElementById("scrollableDiv");
        if (!scrollableElement) {
            return undefined;
        }
        const addedOverflowHidden = !scrollableElement.classList.contains("overflow-hidden");
        const removedOverflowAuto = scrollableElement.classList.contains("overflow-y-auto");
        if (addedOverflowHidden) {
            scrollableElement.classList.add("overflow-hidden");
        }
        if (removedOverflowAuto) {
            scrollableElement.classList.remove("overflow-y-auto");
        }
        return () => {
            if (addedOverflowHidden) {
                scrollableElement.classList.remove("overflow-hidden");
            }
            if (removedOverflowAuto) {
                scrollableElement.classList.add("overflow-y-auto");
            }
        };
    }, [isMdViewport]);

    useEffect(() => {
        if (!canViewMarginData) {
            setShowMargin(false);
            return;
        }
        setShowMargin(readShowMarginPreference(true));
    }, [canViewMarginData]);

    useEffect(() => {
        if (
            !canViewMarginData
            || typeof window === "undefined"
            || typeof window.localStorage === "undefined"
        ) {
            return;
        }

        try {
            window.localStorage.setItem(SHOW_MARGIN_STORAGE_KEY, showMargin ? "true" : "false");
        } catch (error) {
            // Swallow storage errors silently
        }
    }, [canViewMarginData, showMargin]);

    const resolvedDocument = document ?? fetchedDocument ?? null;
    const documentCanConfirm = document?.can_confirm;
    const isDocumentConfirmed = resolvedDocument?.status === CONFIRMED;
    const canEditConnectionVisibility = canEditDocumentPermission;
    const canEditDocument = canEditDocumentPermission && !isDocumentConfirmed;
    const canDeleteDocument = canDeleteDocumentPermission && !isDocumentConfirmed;

    useEffect(() => {
        setDocument(fetchedDocument ?? null);
    }, [fetchedDocument]);

    const removeDetail = useMemo(() => {
        if (!companyId || !documentId) {
            return null;
        }
        return {
            title: t("app.documents.remove_title", {defaultValue: "Rimuovi documento"}),
            message: t("app.documents.remove_description", {defaultValue: "Sei sicuro di voler eliminare questo documento?"}),
            endpoint: getPathParam([COMPANY, String(companyId), "header", type, documentId]),
        };
    }, [companyId, documentId, t, type]);

    const isPackagingConfirmed = useMemo(() => (
        Boolean(
            document?.confirmed_packaging
            ?? document?.packing_confirmed
            ?? document?.is_confirmed_packaging
            ?? false
        )
    ), [document]);

    const statusOptions = useMemo(() => {
        const masterStatuses = document?.master_document_type?.status;
        if (Array.isArray(masterStatuses) && masterStatuses.length > 0) {
            const uniqueStatuses = masterStatuses
                .map((status) => (typeof status === "string" ? status.trim() : ""))
                .filter((status) => Boolean(status));
            if (uniqueStatuses.length > 0) {
                const normalizedMap = new Map();
                uniqueStatuses.forEach((status) => {
                    const normalized = status.toLowerCase();
                    if (!normalizedMap.has(normalized)) {
                        normalizedMap.set(normalized, status);
                    }
                });
                return Array.from(normalizedMap.values());
            }
        }
        return DEFAULT_STATUS_LIST;
    }, [document?.master_document_type?.status]);

    const normalizedStatusOptions = useMemo(
        () => statusOptions.map((status) => normalizeStatusValue(status)),
        [statusOptions]
    );

    const {displayedStatus, statusIsSupported} = useMemo(() => {
        const fallbackStatus = statusOptions[0] ?? DEFAULT_STATUS_LIST[0];
        const normalizedFallback = normalizeStatusValue(fallbackStatus);
        const resolvedStatus = (typeof document?.status === "string" && document.status)
            ? document.status
            : fallbackStatus;
        const normalizedResolvedStatus = normalizeStatusValue(resolvedStatus);
        const supported = normalizedStatusOptions.includes(normalizedResolvedStatus);
        return {
            displayedStatus: supported ? resolvedStatus : fallbackStatus,
            statusIsSupported: supported || normalizedStatusOptions.includes(normalizedFallback),
            normalizedDisplayedStatus: normalizedResolvedStatus,
        };
    }, [document?.status, normalizedStatusOptions, statusOptions]);

    const canEditStatus = statusIsSupported && Boolean(isExporter ?? true);
    const canConfirmStatus = canEditStatus && canConfirmStatusPermission;
    const hasProductStatusManagement = Boolean(company?.product_manage_status);

    useLayoutEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        const element = headerRef.current;
        if (!element) {
            setDocumentHeaderHeight(0);
            return undefined;
        }
        let animationFrameId = null;
        const commitHeaderHeight = () => {
            const nextHeight = element.offsetHeight ?? 0;
            setDocumentHeaderHeight((previous) => (previous === nextHeight ? previous : nextHeight));
        };
        const updateHeaderHeight = () => {
            if (animationFrameId !== null) {
                return;
            }
            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                commitHeaderHeight();
            });
        };
        updateHeaderHeight();
        const observer = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateHeaderHeight)
            : null;
        if (observer) {
            observer.observe(element);
        }
        window.addEventListener("resize", updateHeaderHeight);
        return () => {
            window.removeEventListener("resize", updateHeaderHeight);
            if (observer) {
                observer.disconnect();
            }
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [document, displayedStatus, isAcquisitionUpdating]);

    useLayoutEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            setIsMdViewport(false);
            return undefined;
        }
        const query = window.matchMedia("(min-width: 768px)");
        const handleChange = () => {
            setIsMdViewport(query.matches);
        };
        handleChange();
        if (typeof query.addEventListener === "function") {
            query.addEventListener("change", handleChange);
            return () => query.removeEventListener("change", handleChange);
        }
        query.addListener(handleChange);
        return () => query.removeListener(handleChange);
    }, []);

    const handleToggleMobileDocumentHeader = useCallback(() => {
        setMobileDocumentHeaderOpen((prev) => !prev);
    }, []);

    const handleCloseMobileDocumentHeader = useCallback(() => {
        setMobileDocumentHeaderOpen(false);
    }, []);

    useEffect(() => {
        if (isMdViewport) {
            setMobileDocumentHeaderOpen(false);
        }
    }, [isMdViewport]);

    const resolvedToolbarOffset = useMemo(() => (
        isMdViewport ? 0 : Math.max(0, documentHeaderHeight ?? 0)
    ), [documentHeaderHeight, isMdViewport]);

    const documentTotals = document?.totals ?? {};
    const totalsSectionDefinitions = useMemo(() => ([
        {id: "general", label: t("app.documents.totals.general", {defaultValue: "Generale"})},
        {id: "confirmed", label: t("app.documents.totals.confirmed", {defaultValue: "Confermato"})},
        {id: "residual", label: t("app.documents.totals.residual", {defaultValue: "Residuo"})},
    ]), [t]);

    const totalsBase = documentTotals?.totals ?? documentTotals;
    const availableTotalsSections = totalsSectionDefinitions.filter(({id}) => {
        const sectionData = totalsBase?.[id];
        if (!sectionData || typeof sectionData !== "object") {
            return false;
        }
        return Object.keys(sectionData).length > 0;
    });
    const hasTotalsTabs = availableTotalsSections.length > 0;

    const documentTotalsRaw = document?.document_total
        ?? document?.documentTotal
        ?? {};
    const documentTotalsBase = documentTotalsRaw?.totals ?? documentTotalsRaw;
    const availableDocumentTotalsSections = totalsSectionDefinitions.filter(({id}) => {
        const sectionData = documentTotalsBase?.[id];
        if (!sectionData || typeof sectionData !== "object") {
            return false;
        }
        return Object.keys(sectionData).length > 0;
    });
    const hasDocumentTotalsTabs = availableDocumentTotalsSections.length > 0;
    const hasDocumentTotalsData = documentTotalsBase && Object.keys(documentTotalsBase).length > 0;
    const connectionSignedDate = document?.connection_signed_date ?? null;
    const hasConfirmationInfo = Boolean(document?.signed_date || connectionSignedDate);
    const isConfirmedByCreator = Boolean(document?.signed_date);
    const isConfirmedByCustomer = Boolean(connectionSignedDate);
    const confirmationIconColor = useMemo(() => {
        if (isConfirmedByCreator && isConfirmedByCustomer) {
            return "text-emerald-300";
        }
        if (isConfirmedByCreator && !isConfirmedByCustomer) {
            return "text-amber-300";
        }
        if (!isConfirmedByCreator && isConfirmedByCustomer) {
            return "text-blue-200";
        }
        return "text-white";
    }, [isConfirmedByCreator, isConfirmedByCustomer]);
    const documentSignatureBadgeVariant = useMemo(() => {
        if (isConfirmedByCreator && isConfirmedByCustomer) {
            return "success";
        }
        if (isConfirmedByCustomer) {
            return "info";
        }
        if (isConfirmedByCreator) {
            return "warning";
        }
        return "neutral";
    }, [isConfirmedByCreator, isConfirmedByCustomer]);
    const documentSignatureBadge = useMemo(() => {
        if (documentSignatureBadgeVariant === "success") {
            return t("app.documents.confirmation.status_complete", {defaultValue: "Firmato da entrambi"});
        }
        if (documentSignatureBadgeVariant === "info") {
            return t("app.documents.confirmation.status_partial_other", {defaultValue: "Conferma registrata"});
        }
        if (documentSignatureBadgeVariant === "warning") {
            return t("app.documents.confirmation.status_pending_other", {defaultValue: "In attesa di controfirma"});
        }
        return t("app.documents.confirmation.status_none", {defaultValue: "Nessuna conferma"});
    }, [documentSignatureBadgeVariant, t]);
    const documentSignatureBadgeClass = useMemo(() => {
        if (documentSignatureBadgeVariant === "success") {
            return "border-emerald-200 bg-emerald-50 text-emerald-700";
        }
        if (documentSignatureBadgeVariant === "info") {
            return "border-blue-200 bg-blue-50 text-blue-900";
        }
        if (documentSignatureBadgeVariant === "warning") {
            return "border-amber-200 bg-amber-50 text-amber-700";
        }
        return "border-gray-200 bg-gray-50 text-gray-700";
    }, [documentSignatureBadgeVariant]);
    const documentSignatureCards = useMemo(() => {
        if (!hasConfirmationInfo) {
            return [];
        }
        const items = [];
        if (isExporter && document?.signed_date) {
            const variant = document?.signed_date ? "success" : "warning";
            const IconComponent = document?.signed_date ? CheckIcon : SignatureIcon;
            items.push(
                <SignatureStatusCard
                    key="creator-confirmation"
                    variant={variant}
                    icon={IconComponent}
                    title={t("app.documents.confirmation.creator_title", {defaultValue: "Conferma azienda"})}
                >
                    <span className="block">
                        {t("app.quotations.confirmed_document", {
                            date: getCustomFormat(document?.signed_date, "DD/MM/YYYY"),
                            time: getCustomFormat(document?.signed_date, "HH:mm"),
                        })}
                        {connectionSignedDate ? (
                            <>
                                {" "}
                                {t("app.quotations.second_confirmed_document", {
                                    date_confirm: getCustomFormat(connectionSignedDate, "DD/MM/YYYY"),
                                    user: document?.customer?.business_name ?? "",
                                    time: getCustomFormat(connectionSignedDate, "HH:mm"),
                                })}
                            </>
                        ) : null}
                        .
                    </span>
                </SignatureStatusCard>
            );
        }
        if (isExporter && !document?.creator_confirmed_date && connectionSignedDate) {
            items.push(
                <SignatureStatusCard
                    key="customer-confirmation-exporter"
                    variant="info"
                    icon={CheckIcon}
                    title={t("app.documents.confirmation.customer_title", {defaultValue: "Conferma cliente"})}
                >
                    <span className="block">
                        {t("app.quotations.user_confirmed_document", {
                            date_confirm: getCustomFormat(connectionSignedDate, "DD/MM/YYYY"),
                            user: document?.customer ? (`${document.customer.business_name} `) : "",
                            time: getCustomFormat(connectionSignedDate, "HH:mm"),
                        })}
                        .
                    </span>
                </SignatureStatusCard>
            );
        }
        if (!isExporter && connectionSignedDate) {
            const variant = document?.creator_confirmed_date ? "success" : "info";
            items.push(
                <SignatureStatusCard
                    key="customer-confirmation-importer"
                    variant={variant}
                    icon={CheckIcon}
                    title={t("app.documents.confirmation.customer_title", {defaultValue: "Conferma cliente"})}
                >
                    <span className="block">
                        {t("app.quotations.confirmed_document", {
                            date: getCustomFormat(connectionSignedDate, "DD/MM/YYYY"),
                            time: getCustomFormat(connectionSignedDate, "HH:mm"),
                        })}
                        {document?.creator_confirmed_date ? (
                            <>
                                {" "}
                                {t("app.quotations.second_confirmed_document", {
                                    date_confirm: getCustomFormat(document?.creator_confirmed_date, "DD/MM/YYYY"),
                                    time: getCustomFormat(document?.creator_confirmed_date, "HH:mm"),
                                    user: document?.customer?.business_name ?? "",
                                })}
                            </>
                        ) : null}
                        .
                    </span>
                </SignatureStatusCard>
            );
        }
        if (!isExporter && document?.creator_confirmed_date && !connectionSignedDate) {
            items.push(
                <SignatureStatusCard
                    key="creator-confirmation-importer"
                    variant="warning"
                    icon={SignatureIcon}
                    title={t("app.documents.confirmation.creator_title", {defaultValue: "Conferma azienda"})}
                >
                    <span className="block">
                        {t("app.quotations.user_confirmed_document", {
                            date_confirm: getCustomFormat(document?.creator_confirmed_date, "DD/MM/YYYY"),
                            user: document?.customer ? (`${document.customer.business_name} `) : "",
                            time: getCustomFormat(document?.creator_confirmed_date, "HH:mm"),
                        })}
                        .
                    </span>
                </SignatureStatusCard>
            );
        }
        return items;
    }, [
        connectionSignedDate,
        document,
        hasConfirmationInfo,
        isExporter,
        t,
    ]);

    useEffect(() => {
        if (!hasDocumentTotalsData) {
            setDocumentTotalsVisible(false);
        }
    }, [hasDocumentTotalsData]);

    useEffect(() => {
        const allAvailableSections = totalsSectionDefinitions.filter(({id}) => (
            availableTotalsSections.some((section) => section.id === id)
            || availableDocumentTotalsSections.some((section) => section.id === id)
        ));

        if (allAvailableSections.length === 0) {
            return;
        }

        const nextSection = allAvailableSections[0]?.id ?? "general";
        if (!allAvailableSections.some(({id}) => id === selectedTotalsSection)) {
            setSelectedTotalsSection(nextSection);
        }
    }, [availableDocumentTotalsSections, availableTotalsSections, selectedTotalsSection, totalsSectionDefinitions]);

    const fallbackTotals = useMemo(() => (
        hasTotalsTabs
            ? (totalsBase?.general ?? totalsBase ?? {})
            : (totalsBase ?? {})
    ), [hasTotalsTabs, totalsBase]);
    const activeTotals = useMemo(() => (
        hasTotalsTabs
            ? (totalsBase?.[selectedTotalsSection] ?? {})
            : (totalsBase ?? {})
    ), [hasTotalsTabs, selectedTotalsSection, totalsBase]);
    const documentTotalsFallback = useMemo(() => (
        hasDocumentTotalsTabs
            ? (documentTotalsBase?.general ?? documentTotalsBase ?? {})
            : (documentTotalsBase ?? {})
    ), [documentTotalsBase, hasDocumentTotalsTabs]);
    const documentTotalsActive = useMemo(() => (
        hasDocumentTotalsTabs
            ? (documentTotalsBase?.[selectedTotalsSection] ?? {})
            : (documentTotalsBase ?? {})
    ), [documentTotalsBase, hasDocumentTotalsTabs, selectedTotalsSection]);
    const currencyCode = document?.currency?.code ?? document?.currency ?? null;
    const currencySymbol = document?.currency?.currency_symbol ?? "";

    const totalValue = parseNumericValue(activeTotals.total_discounted)
        ?? parseNumericValue(activeTotals.total)
        ?? parseNumericValue(activeTotals.total_purchase_price)
        ?? parseNumericValue(fallbackTotals.total_discounted)
        ?? parseNumericValue(fallbackTotals.total)
        ?? parseNumericValue(fallbackTotals.total_purchase_price)
        ?? parseNumericValue(document?.total)
        ?? parseNumericValue(document?.total_amount);

    const resolvedDocumentId = useMemo(() => {
        if (document?.id) {
            return String(document.id);
        }
        if (documentId) {
            return String(documentId);
        }
        return null;
    }, [document?.id, documentId]);

    useEffect(() => {
        setValidationSummary([]);
        setValidationModalOpen(false);
    }, [resolvedDocumentId]);

    useEffect(() => {
        setExternalConfirmedQuantityRequest(null);
        setRequestEventsRefreshKey((value) => value + 1);
    }, [resolvedDocumentId]);

    const isRefreshingTotals = totalsRefreshCount > 0;

    // Resolve cloning status from document payload using common key variants
    const resolveCloningStatus = useCallback((payload) => {
        if (!payload || typeof payload !== "object") {
            return null;
        }
        const candidates = [
            payload.cloning_status,
            payload.cloningStatus,
            payload.body_cloning_status,
            payload.bodyCloningStatus,
            payload?.body?.cloning_status,
            payload?.body?.cloningStatus,
        ];
        const match = candidates.find((entry) => typeof entry === "string" && entry.trim() !== "");
        return match ? String(match).toLowerCase() : null;
    }, []);



    const handleRefreshDocumentDetail = useCallback(async () => {
        if (!companyId || !resolvedDocumentId || !type) {
            return null;
        }

        setTotalsRefreshCount((count) => count + 1);
        try {
            const response = await fetchDocumentDetail({
                companyId,
                type,
                documentId: resolvedDocumentId,
            });
            if (response) {
                setDocument(response ?? null);
            }
            return response ?? null;
        } catch (error) {
            console.error("Failed to refresh document detail", error);
            return null;
        } finally {
            setTotalsRefreshCount((count) => Math.max(0, count - 1));
        }
    }, [companyId, resolvedDocumentId, setDocument, setTotalsRefreshCount, type]);

    const handleRequestEventSelection = useCallback((eventItem) => {
        if (!eventItem) {
            return;
        }
        setExternalConfirmedQuantityRequest({
            event: eventItem,
            key: `${eventItem?.id ?? "event"}-${Date.now()}`,
        });
        if (documentBodySectionRef.current) {
            documentBodySectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, []);

    const handleConfirmedQuantityEventsUpdated = useCallback(() => {
        setRequestEventsRefreshKey((value) => value + 1);
    }, []);

    // Start/stop polling the document detail while cloning is in progress
    useEffect(() => {
        const status = resolveCloningStatus(document);
        const previousStatus = cloningLastStatusRef.current;
        const isInProgress = status === "in_progress" || status === "processing" || status === "running";
        const isDone = status === "done" || status === "completed" || status === "complete";

        if (isInProgress) {
            if (!isCloningPopupOpen) {
                setIsCloningPopupOpen(true);
            }
            if (!cloningPollerRef.current) {
                cloningPollerRef.current = setInterval(() => {
                    // Leverage existing refresh function to keep document up to date
                    handleRefreshDocumentDetail();
                }, 2000);
            }
        }

        if (isDone && previousStatus !== "done" && previousStatus !== "completed" && previousStatus !== "complete") {
            if (cloningPollerRef.current) {
                clearInterval(cloningPollerRef.current);
                cloningPollerRef.current = null;
            }
            if (isCloningPopupOpen) {
                setIsCloningPopupOpen(false);
            }
            // Refresh body rows once cloning completes
            setImportRefreshKey((previous) => previous + 1);
            // Ensure header/totals reflect cloned rows
            handleRefreshDocumentDetail();
        }

        cloningLastStatusRef.current = status;

        return () => {
            // On unmount or document change, do not leak timers
            // Note: timer is conditionally cleared above when status switches to done
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [document, resolveCloningStatus, handleRefreshDocumentDetail]);

    // Defensive cleanup on unmount
    useEffect(() => () => {
        if (cloningPollerRef.current) {
            clearInterval(cloningPollerRef.current);
            cloningPollerRef.current = null;
        }
    }, []);

    const formatCurrencyValue = useCallback((value) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return "-";
        }

        if (currencyCode) {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: "currency",
                    currency: currencyCode,
                    maximumFractionDigits: 2,
                }).format(numeric);
            } catch (error) {
                // ignore and fall back to manual formatting
            }
        }

        const formatted = numeric.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
    }, [currencyCode, currencySymbol]);

    const formatCountValue = useCallback((value) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return "-";
        }

        return numeric.toLocaleString(undefined, {
            maximumFractionDigits: 0,
        });
    }, []);

    const formatMeasurementValue = useCallback((value) => {
        const numeric = parseNumericValue(value);
        if (numeric === null) {
            return "-";
        }

        return numeric.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 3,
        });
    }, []);

    const totalPurchasePrice = parseNumericValue(activeTotals.total_purchase_price)
        ?? parseNumericValue(fallbackTotals.total_purchase_price);
    const totalNetPrice = parseNumericValue(activeTotals.total_net_price)
        ?? parseNumericValue(fallbackTotals.total_net_price);
    const totalNetPriceWithoutDiscount = parseNumericValue(activeTotals.total_net_price_without_discount)
        ?? parseNumericValue(fallbackTotals.total_net_price_without_discount);
    const totalUnitPriceRaw = parseNumericValue(activeTotals.total_unit_price)
        ?? parseNumericValue(fallbackTotals.total_unit_price)
        ?? totalNetPrice
        ?? totalValue
        ?? 0;
    const totalDiscount = parseNumericValue(activeTotals.total_discount)
        ?? parseNumericValue(fallbackTotals.total_discount);

    const formattedTotal = useMemo(
        () => formatCurrencyValue(totalNetPrice),
        [formatCurrencyValue, totalNetPrice]
    );

    const normalizedNetPrice = Number.isFinite(totalNetPrice) ? totalNetPrice : null;
    const normalizedNetPriceWithoutDiscount = Number.isFinite(totalNetPriceWithoutDiscount)
        ? totalNetPriceWithoutDiscount
        : null;
    const normalizedPurchasePrice = Number.isFinite(totalPurchasePrice) ? totalPurchasePrice : null;
    const hasPriceRange = normalizedNetPrice !== null
        && normalizedPurchasePrice !== null
        && normalizedNetPrice > 0
        && normalizedPurchasePrice > 0;
    const discountBaseValue = normalizedNetPriceWithoutDiscount ?? normalizedNetPrice;
    const formattedOriginalTotal = useMemo(() => (
        Number.isFinite(totalNetPriceWithoutDiscount)
            ? formatCurrencyValue(totalNetPriceWithoutDiscount)
            : null
    ), [formatCurrencyValue, totalNetPriceWithoutDiscount]);
    const showOriginalTotal = normalizedNetPriceWithoutDiscount !== null
        && normalizedNetPrice !== null
        && Math.abs(normalizedNetPriceWithoutDiscount - normalizedNetPrice) > 0.01;
    const documentDiscountPercent = useMemo(
        () => normalizeDiscountPercent(document?.discount),
        [document?.discount]
    );
    const documentDiscountValue = useMemo(() => {
        if (documentDiscountPercent === null || discountBaseValue === null) {
            return null;
        }
        return (discountBaseValue * documentDiscountPercent) / 100;
    }, [discountBaseValue, documentDiscountPercent]);
    useEffect(() => {
        if (isDiscountPopupOpen) {
            return;
        }
        setDiscountPercentInput(documentDiscountPercent !== null ? formatEditableNumber(documentDiscountPercent, 2) : "");
        setDiscountValueInput(documentDiscountValue !== null ? formatEditableNumber(documentDiscountValue, 2) : "");
    }, [documentDiscountPercent, documentDiscountValue, isDiscountPopupOpen]);
    useEffect(() => {
        if (isDiscountPopupOpen) {
            return;
        }
        setDiscountMode("percent");
    }, [isDiscountPopupOpen]);
    const canUseDiscountValueMode = discountBaseValue !== null && discountBaseValue > 0;
    const resolvedDiscountPercent = useMemo(() => {
        if (discountMode === "value") {
            const parsedValue = parseEditableNumber(discountValueInput);
            if (parsedValue === null || !canUseDiscountValueMode || discountBaseValue === null) {
                return null;
            }
            const percent = (parsedValue / discountBaseValue) * 100;
            return clampDiscountPercent(percent);
        }
        const parsedPercent = parseEditableNumber(discountPercentInput);
        if (parsedPercent === null && discountPercentInput.trim() !== "") {
            return null;
        }
        if (parsedPercent === null) {
            return null;
        }
        return clampDiscountPercent(parsedPercent);
    }, [canUseDiscountValueMode, discountBaseValue, discountMode, discountPercentInput, discountValueInput]);
    const previewDiscountAmount = useMemo(() => {
        if (discountBaseValue === null || resolvedDiscountPercent === null) {
            return null;
        }
        const value = (discountBaseValue * resolvedDiscountPercent) / 100;
        return Math.min(Math.max(value, 0), discountBaseValue);
    }, [discountBaseValue, resolvedDiscountPercent]);
    const previewDiscountedTotal = useMemo(() => {
        if (discountBaseValue === null) {
            return null;
        }
        if (previewDiscountAmount === null) {
            return discountBaseValue;
        }
        return Math.max(0, discountBaseValue - previewDiscountAmount);
    }, [discountBaseValue, previewDiscountAmount]);
    const discountHasChanged = useMemo(() => {
        if (resolvedDiscountPercent === null) {
            return false;
        }
        const initialPercent = documentDiscountPercent ?? 0;
        return Math.abs(resolvedDiscountPercent - initialPercent) > 0.001;
    }, [documentDiscountPercent, resolvedDiscountPercent]);
    const isDiscountActionDisabled = isDiscountUpdating || resolvedDiscountPercent === null || !discountHasChanged;
    const previewTotalFormatted = previewDiscountedTotal !== null ? formatCurrencyValue(previewDiscountedTotal) : "-";
    const previewDiscountFormatted = previewDiscountAmount !== null ? formatCurrencyValue(previewDiscountAmount) : "-";
    const resolvedPercentLabel = resolvedDiscountPercent !== null ? `${resolvedDiscountPercent.toFixed(2)}%` : null;
    const activeDiscountPercentLabel = documentDiscountPercent !== null && documentDiscountPercent > 0
        ? `-${documentDiscountPercent.toFixed(1)}%`
        : null;

    const totalRowsMetricRaw = parseNumericValue(documentTotalsActive.total_rows)
        ?? parseNumericValue(documentTotalsFallback.total_rows);
    const totalVolumeMetricRaw = parseNumericValue(documentTotalsActive.total_volume)
        ?? parseNumericValue(documentTotalsFallback.total_volume);
    const totalNetWeightMetricRaw = parseNumericValue(documentTotalsActive.total_net_weight)
        ?? parseNumericValue(documentTotalsFallback.total_net_weight);
    const totalGrossWeightMetricRaw = parseNumericValue(documentTotalsActive.total_gross_weight)
        ?? parseNumericValue(documentTotalsFallback.total_gross_weight);
    const totalBoxMetricRaw = parseNumericValue(documentTotalsActive.total_box)
        ?? parseNumericValue(documentTotalsFallback.total_box);
    const totalBoxHasFraction = totalBoxMetricRaw !== null && !Number.isInteger(totalBoxMetricRaw);
    const totalBoxDisplayValue = totalBoxHasFraction
        ? formatMeasurementValue(totalBoxMetricRaw)
        : formatCountValue(totalBoxMetricRaw);

    const totalsBreakdown = useMemo(() => ([
        {
            id: "total-purchase-price",
            label: t("app.documents.total_purchase_price", {defaultValue: "Totale acquisto"}),
            value: formatCurrencyValue(totalPurchasePrice),
        },
        {
            id: "total-unit-price",
            label: t("app.documents.total_unit_price", {defaultValue: "Totale unitario"}),
            value: formatCurrencyValue(totalUnitPriceRaw),
        },
        {
            id: "total-discount",
            label: t("app.documents.total_discount", {defaultValue: "Totale sconti"}),
            value: formatCurrencyValue(totalDiscount),
        },
        {
            id: "total-net-price",
            label: t("app.documents.total", {defaultValue: "Totale"}),
            value: formattedTotal,
            isHighlight: true,
        }
    ]), [
        formatCurrencyValue,
        formattedTotal,
        t,
        totalDiscount,
        totalPurchasePrice,
        totalUnitPriceRaw,
    ]);

    const documentTotalsBreakdown = useMemo(() => ([
        {
            id: "document-total-rows",
            label: t("app.documents.metrics.total_rows", {defaultValue: "Righe totali"}),
            value: formatCountValue(totalRowsMetricRaw),
        },
        {
            id: "document-total-volume",
            label: t("app.documents.metrics.total_volume", {defaultValue: "Volume totale (m³)"}),
            value: formatMeasurementValue(totalVolumeMetricRaw),
        },
        {
            id: "document-total-net-weight",
            label: t("app.documents.metrics.total_net_weight", {defaultValue: "Peso netto totale (kg)"}),
            value: formatMeasurementValue(totalNetWeightMetricRaw),
        },
        {
            id: "document-total-gross-weight",
            label: t("app.documents.metrics.total_gross_weight", {defaultValue: "Peso lordo totale (kg)"}),
            value: formatMeasurementValue(totalGrossWeightMetricRaw),
        },
        {
            id: "document-total-box",
            label: t("app.documents.metrics.total_box", {defaultValue: "Box totali"}),
            value: totalBoxDisplayValue,
            warning: totalBoxHasFraction
                ? t("app.documents.metrics.total_box_warning", {defaultValue: "Confezionamento non valido"})
                : null
        },
    ]), [
        formatCountValue,
        formatMeasurementValue,
        t,
        totalBoxDisplayValue,
        totalGrossWeightMetricRaw,
        totalNetWeightMetricRaw,
        totalRowsMetricRaw,
        totalVolumeMetricRaw,
        totalBoxHasFraction,
    ]);

    // Unified outside click handler for totals menus
    useEffect(() => {
        if (!areTotalsVisible && !areDocumentTotalsVisible) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (totalsRef.current && !totalsRef.current.contains(event.target)) {
                if (areTotalsVisible) setTotalsVisible(false);
                if (areDocumentTotalsVisible) setDocumentTotalsVisible(false);
            }
        };

        const doc = typeof window !== "undefined" ? window.document : null;
        if (!doc) {
            return undefined;
        }

        doc.addEventListener("mousedown", handleClickOutside);
        return () => {
            doc.removeEventListener("mousedown", handleClickOutside);
        };
    }, [areTotalsVisible, areDocumentTotalsVisible]);

    const formattedDocumentDate = useMemo(() => {
        const rawDate = document?.document_date
            ?? document?.documentDate
            ?? document?.date
            ?? null;

        if (!rawDate) {
            return "-";
        }

        try {
            return getFormattedDate(rawDate);
        } catch (error) {
            return rawDate;
        }
    }, [document]);

    const handleNavigateBack = useBackNavigation(slug ? buildListPath(slug) : null);

    const handleEdit = () => {
        if (slug && documentId) {
            navigate(buildEditPath(slug, documentId));
        }
    };

    const handlePartialUpdate = useCallback(async (data) => {
        const payload = data ?? {};
        const shouldRefreshBody = Boolean(payload.change_body_delivery_date);
        const payloadKeys = Object.keys(payload);
        const isConnectionVisibilityUpdate = payloadKeys.includes("connection_can_view")
            && payloadKeys.every((key) => ["connection_can_view", "id"].includes(key));
        const isStatusUpdate = payloadKeys.includes("status")
            && payloadKeys.every((key) => ["status", "id"].includes(key));
        const normalizedPayloadStatus = isStatusUpdate ? normalizeStatusValue(payload.status) : "";
        const isConfirmStatusUpdate = isStatusUpdate && normalizedPayloadStatus === NORMALIZED_CONFIRMED_STATUS;

        if (
            !canEditDocument
            && !(
                (isConnectionVisibilityUpdate && canEditConnectionVisibility)
                || (isStatusUpdate && canEditStatus)
            )
        ) {
            throw new Error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
        }

        if (isConfirmStatusUpdate && !canConfirmStatusPermission) {
            throw new Error(t("app.documents.status_confirm_permission_denied", {
                defaultValue: "Non hai i permessi per confermare il documento",
            }));
        }

        const targetDocumentId = resolvedDocumentId ?? documentId;

        if (!companyId || !targetDocumentId || !type) {
            throw new Error("Missing parameters to update document");
        }

        const response = await updateDocument({
            companyId,
            type,
            documentId: targetDocumentId,
            data: payload,
        });

        let resolvedDocument = response ?? null;

        try {
            const refreshed = await fetchDocumentDetail({
                companyId,
                type,
                documentId: targetDocumentId,
            });

            if (refreshed) {
                resolvedDocument = refreshed;
            }
        } catch (refreshError) {
            console.error("Failed to refresh document after update", refreshError);
        }

        setDocument(resolvedDocument);
        if (shouldRefreshBody) {
            setImportRefreshKey((previous) => previous + 1);
        }
        return resolvedDocument;
    }, [canConfirmStatusPermission, canEditConnectionVisibility, canEditDocument, canEditStatus, companyId, documentId, resolvedDocumentId, setImportRefreshKey, t, type]);

    const handleDiscountSave = useCallback(async () => {
        if (!canEditDocument || resolvedDiscountPercent === null) {
            return;
        }
        const payloadDiscount = Number(resolvedDiscountPercent.toFixed(4));
        const payload = resolvedDocumentId
            ? {discount: payloadDiscount, id: resolvedDocumentId}
            : {discount: payloadDiscount};
        setIsDiscountUpdating(true);
        try {
            await handlePartialUpdate(payload);
            toast.success(t("app.documents.discount_modal.success", {defaultValue: "Sconto aggiornato"}));
            setIsDiscountPopupOpen(false);
        } catch (error) {
            toast.error(error?.message ?? t("app.documents.discount_modal.error", {defaultValue: "Impossibile aggiornare lo sconto"}));
        } finally {
            setIsDiscountUpdating(false);
        }
    }, [canEditDocument, handlePartialUpdate, resolvedDiscountPercent, resolvedDocumentId, t]);

    const handleDiscountReset = useCallback(() => {
        setDiscountPercentInput("0");
        setDiscountValueInput(canUseDiscountValueMode ? formatEditableNumber(0, 2) : "");
    }, [canUseDiscountValueMode]);

    const handleOpenDiscountPopup = useCallback(() => {
        if (!canEditDocument) {
            return;
        }
        setIsDiscountPopupOpen(true);
    }, [canEditDocument]);

    const handleStatusChange = useCallback(async (nextStatus, options = {}) => {
        const {confirmQuantity = false} = options ?? {};
        const normalizedNextStatus = normalizeStatusValue(nextStatus);
        const currentStatus = typeof document?.status === "string" ? document.status : null;
        const normalizedCurrentStatus = normalizeStatusValue(currentStatus);
        if (
            !document
            || !statusIsSupported
            || !canEditStatus
            || !nextStatus
            || normalizedNextStatus === normalizedCurrentStatus
            || !normalizedStatusOptions.includes(normalizedNextStatus)
        ) {
            return;
        }

        if (normalizedNextStatus === NORMALIZED_CONFIRMED_STATUS && !canConfirmStatus) {
            toast.error(t("app.documents.status_confirm_permission_denied", {
                defaultValue: "Non hai i permessi per confermare il documento",
            }));
            return;
        }

        setStatusUpdating(true);
        try {
            const payloadStatus = normalizedNextStatus || nextStatus;
            const payload = resolvedDocumentId
                ? {status: payloadStatus, id: resolvedDocumentId}
                : {status: payloadStatus};
            if (confirmQuantity) {
                payload.confirm_quantity = true;
            }
            const response = await handlePartialUpdate(payload);
            if (!response) {
                setDocument((previous) => (previous ? {...previous, status: payloadStatus} : previous));
            }
            toast.success(t("app.document.update_success", {defaultValue: "Documento aggiornato"}));
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setStatusUpdating(false);
        }
    }, [canConfirmStatus, canEditStatus, document, handlePartialUpdate, normalizedStatusOptions, resolvedDocumentId, statusIsSupported, t]);

    const handleToggleConfirmPackaging = useCallback((nextOpen, options = {}) => {
        const resolvedOpen = Boolean(nextOpen);
        setOpenConfirmPackaging(resolvedOpen);
        if (!resolvedOpen) {
            const preservePendingStatus = Boolean(options?.preservePendingStatus);
            if (!preservePendingStatus) {
                setPendingStatus(null);
            }
            setOpenType("");
        }
    }, []);

    const resolveBodyItems = useCallback((payload) => {
        if (!payload) {
            return [];
        }
        if (Array.isArray(payload)) {
            return payload;
        }
        if (Array.isArray(payload.bodies)) {
            return payload.bodies;
        }
        if (Array.isArray(payload.items)) {
            return payload.items;
        }
        if (Array.isArray(payload.body)) {
            return payload.body;
        }
        if (Array.isArray(payload.products)) {
            return payload.products;
        }
        return [];
    }, []);

    const resolveRequiredFieldsDetail = useCallback(() => {
        const details = [];
        const appendDetail = (label) => {
            if (!label || typeof label !== "string") {
                return;
            }
            const translated = t(label, {defaultValue: label});
            if (translated) {
                details.push({title: translated});
            }
        };

        const documentRequiredFields = document?.required_fields;
        if (Array.isArray(documentRequiredFields)) {
            documentRequiredFields.forEach((value) => {
                if (typeof value === "string") {
                    appendDetail(value);
                } else if (value && typeof value === "object" && typeof value.title === "string") {
                    appendDetail(value.title);
                }
            });
        } else if (documentRequiredFields && typeof documentRequiredFields === "object") {
            Object.values(documentRequiredFields).forEach((value) => {
                if (typeof value === "string") {
                    appendDetail(value);
                } else if (value && typeof value === "object") {
                    const title = typeof value.title === "string"
                        ? value.title
                        : typeof value.label === "string"
                            ? value.label
                            : typeof value.translation === "string"
                                ? value.translation
                                : typeof value.titleTranslation === "string"
                                    ? value.titleTranslation
                                    : null;
                    if (title) {
                        appendDetail(title);
                    }
                }
            });
        }

        if (!details.length && company?.required_fields?.product) {
            const productFields = company.required_fields.product;
            const entries = [
                ...(Object.values(productFields.fields ?? {}) ?? []),
                ...(Object.values(productFields.virtual_fields ?? {}) ?? []),
            ];
            entries.forEach((value) => {
                if (value && typeof value === "object") {
                    const title = typeof value.titleTranslation === "string"
                        ? value.titleTranslation
                        : typeof value.title === "string"
                            ? value.title
                            : null;
                    if (title) {
                        appendDetail(title);
                    }
                }
            });
        }

        return details;
    }, [company?.required_fields, document?.required_fields, t]);

    const resolveUnconfirmedProductStatuses = useCallback(async () => {
        if (!companyId || !resolvedDocumentId) {
            return [];
        }
        try {
            const response = await fetchDocumentBody({
                companyId,
                documentId: resolvedDocumentId,
            });
            const items = resolveBodyItems(response);
            if (!Array.isArray(items) || items.length === 0) {
                return [];
            }

            const unresolved = [];
            items.forEach((item, index) => {
                if (!item) {
                    return;
                }
                const product = item?.product;
                if (!product) {
                    return;
                }
                // if (!item.hide) {
                //     return;
                // }
                const productStatusRaw = product?.status ?? "";
                const normalizedProductStatus = normalizeStatusValue(productStatusRaw);
                if (normalizedProductStatus !== "approved") {
                    if (item.hide) {
                        return;
                    }
                    const productName = item?.name
                        ?? item?.product?.name
                        ?? `${t("app.common.row", {defaultValue: "Riga"})} ${index + 1}`;
                    const resolvedStatusLabel = productStatusRaw
                        ? t(`app.products.status_${normalizedProductStatus}`, {defaultValue: productStatusRaw})
                        : t("app.products.status_missing", {defaultValue: "Stato mancante"});
                    unresolved.push({
                        title: t("app.documents.product_status_not_approved", {
                            defaultValue: `Il prodotto "{{product}}" è in stato "{{status}}": impostalo su "approved" per proseguire.`,
                            product: productName,
                            status: resolvedStatusLabel,
                        }),
                    });
                }
            });
            return unresolved;
        } catch (error) {
            console.error("Failed to verify product status", error);
            toast.error(t("app.documents.body_validation_error", {defaultValue: "Impossibile verificare la confezione"}));
            return [{
                title: t("app.documents.product_status_check_error", {
                    defaultValue: "Impossibile verificare lo stato dei prodotti.",
                }),
            }];
        }
    }, [companyId, resolveBodyItems, resolvedDocumentId, t]);

    const checkPackagingIssues = useCallback(async () => {
        if (!companyId || !resolvedDocumentId) {
            return false;
        }
        try {
            const response = await fetchDocumentBody({
                companyId,
                documentId: resolvedDocumentId,
            });
            const items = resolveBodyItems(response);
            if (!Array.isArray(items) || items.length === 0) {
                return false;
            }
            return items.some((item) => {
                if (!item) {
                    return false;
                }
                const hasProductReference = Boolean(
                    item?.product
                    || item?.product_id
                    || item?.catalog_product_id
                    || item?.product_reference
                );
                if (!hasProductReference) {
                    return false;
                }
                const packaging = Array.isArray(item?.product?.product_dimensions)
                    ? item.product.product_dimensions.find((dimension) => dimension?.default_dimension)
                    : null;
                const quantity = Number(item?.quantity ?? 0);
                const packagingQty = Number(item?.qty_per_packaging ?? packaging?.qty_per_packaging ?? 0);
                if (packagingQty <= 0) {
                    return true;
                }
                return quantity > 0 && packagingQty > 0 && quantity % packagingQty !== 0;
            });
        } catch (error) {
            console.error("Failed to validate packaging", error);
            toast.error(t("app.documents.body_validation_error", {defaultValue: "Impossibile verificare la confezione"}));
            return true;
        }
    }, [companyId, resolveBodyItems, resolvedDocumentId, t]);

    const requestPackagingConfirmation = useCallback(async ({nextAction = "", preservePendingStatus = false} = {}) => {
        if (isPackagingConfirmed) {
            return false;
        }
        const hasIssues = await checkPackagingIssues();
        if (!hasIssues) {
            return false;
        }
        setOpenType(nextAction);
        handleToggleConfirmPackaging(true, {preservePendingStatus});
        return true;
    }, [checkPackagingIssues, handleToggleConfirmPackaging, isPackagingConfirmed]);

    const handleStatusSelection = useCallback(async (nextStatus) => {
        const normalizedNextStatus = normalizeStatusValue(nextStatus);
        if (!normalizedNextStatus) {
            return;
        }

        const currentStatus = typeof document?.status === "string" ? document.status : null;
        const normalizedCurrentStatus = normalizeStatusValue(currentStatus);
        if (
            !document
            || !statusIsSupported
            || !canEditStatus
            || !normalizedStatusOptions.includes(normalizedNextStatus)
            || normalizedNextStatus === normalizedCurrentStatus
        ) {
            return;
        }

        if (
            normalizedNextStatus === NORMALIZED_CONFIRMED_STATUS
            && hasProductStatusManagement
        ) {
            const productStatusIssues = await resolveUnconfirmedProductStatuses();
            if (productStatusIssues.length > 0) {
                setRequiredDetail(productStatusIssues);
                setPendingStatus(null);
                setOpenRequird(true);
                return;
            }

            if (documentCanConfirm === false) {
                const fallbackRequired = resolveRequiredFieldsDetail();
                setRequiredDetail(fallbackRequired.length > 0
                    ? fallbackRequired
                    : [{
                        title: t("app.documents.product_status_generic_error", {
                            defaultValue: "Aggiorna lo stato dei prodotti prima di confermare.",
                        }),
                    }]);
                setPendingStatus(null);
                setOpenRequird(true);
                return;
            }
        }

        if (normalizedNextStatus === NORMALIZED_DRAFT_STATUS) {
            handleStatusChange(nextStatus);
            setPendingStatus(null);
            return;
        }

        if (normalizedNextStatus === NORMALIZED_REVIEW_STATUS) {
            setPendingStatus(nextStatus);
            setStatusConfirmDetail({
                title: t("app.quotations.status_confirm_title"),
                message: t("app.quotations.status_confirm_description", {
                    status: t(`app.quotations.status_${normalizedNextStatus}`),
                }),
            });
            setStatusConfirmCheckOpen(true);
            return;
        }

        if (normalizedNextStatus === NORMALIZED_CONFIRMED_STATUS) {
            setPendingStatus(nextStatus);
            if (!isPackagingConfirmed) {
                const blockedByPackaging = await requestPackagingConfirmation({
                    nextAction: nextStatus || CONFIRMED,
                    preservePendingStatus: true,
                });
                if (blockedByPackaging) {
                    return;
                }
            }
            if (canManageQuantityConfirmation) {
                setConfirmQuantityOpen(true);
                return;
            }
            handleStatusChange(nextStatus);
            setPendingStatus(null);
            return;
        }

        if (normalizedNextStatus === NORMALIZED_CANCELED_STATUS) {
            handleStatusChange(nextStatus);
            setPendingStatus(null);
            return;
        }

        handleStatusChange(nextStatus);
        setPendingStatus(null);
    }, [
        canEditStatus,
        canManageQuantityConfirmation,
        documentCanConfirm,
        document,
        handleStatusChange,
        hasProductStatusManagement,
        isPackagingConfirmed,
        normalizedStatusOptions,
        requestPackagingConfirmation,
        resolveRequiredFieldsDetail,
        resolveUnconfirmedProductStatuses,
        statusIsSupported,
        t,
    ]);

    const handleCloseStatusConfirmCheck = useCallback(() => {
        setStatusConfirmCheckOpen(false);
        setStatusConfirmDetail(null);
        setPendingStatus(null);
    }, []);

    const handleConfirmStatusFromCheck = useCallback(() => {
        if (pendingStatus) {
            handleStatusChange(pendingStatus);
        }
        setPendingStatus(null);
        setStatusConfirmDetail(null);
        setStatusConfirmCheckOpen(false);
    }, [handleStatusChange, pendingStatus]);

    const handleResolvePendingStatus = useCallback((shouldConfirmQuantity) => {
        if (!pendingStatus) {
            setConfirmQuantityOpen(false);
            setPendingStatus(null);
            return;
        }
        if (shouldConfirmQuantity) {
            handleStatusChange(pendingStatus, {confirmQuantity: true});
        } else {
            handleStatusChange(pendingStatus);
        }
        setPendingStatus(null);
        setConfirmQuantityOpen(false);
    }, [handleStatusChange, pendingStatus]);

    const handleConfirmQuantityStatus = useCallback(() => {
        handleResolvePendingStatus(true);
    }, [handleResolvePendingStatus]);

    const handleSkipQuantityStatus = useCallback(() => {
        handleResolvePendingStatus(false);
    }, [handleResolvePendingStatus]);

    const handleToggleConfirmQuantity = useCallback((nextOpen) => {
        const resolvedOpen = Boolean(nextOpen);
        setConfirmQuantityOpen(resolvedOpen);
        if (!resolvedOpen) {
            setPendingStatus(null);
        }
    }, []);

    const handleAcquisitionStatusChange = useCallback(async (nextStatus) => {
        if (!document || !canEditDocument || !nextStatus || nextStatus === document.acquisition_status) {
            return;
        }

        setAcquisitionUpdating(true);
        try {
            const payload = resolvedDocumentId
                ? {acquisition_status: nextStatus, id: resolvedDocumentId}
                : {acquisition_status: nextStatus};
            const response = await handlePartialUpdate(payload);
            if (!response) {
                setDocument((previous) => (previous ? {...previous, acquisition_status: nextStatus} : previous));
            }
            toast.success(t("app.document.update_success", {defaultValue: "Documento aggiornato"}));
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setAcquisitionUpdating(false);
        }
    }, [canEditDocument, document, handlePartialUpdate, resolvedDocumentId, t]);

    const handleImportQueued = useCallback((importJob) => {
        setLatestImport(importJob ?? null);
    }, []);

    const handleImportNeedRefresh = useCallback((result) => {
        setLatestImport(result ?? null);
        setImportRefreshKey((previous) => previous + 1);
        if (result?.status === "done") {
            toast.success(t("app.documents.body_import_success", {defaultValue: "Righe importate"}));
        } else if (result?.status === "error") {
            toast.error(t("app.import_log.import_error", {defaultValue: "Importazione non riuscita"}));
        }
    }, [t]);

    const documentReportDetails = useMemo(() => ({
        type:"document",
        entity: "document",
        code: "document",
        config: {
            isConfirmable: false,
            isMultiGenerate: true,
        },
        configPdfParams: [
            {
                type: 'checkbox',
                name: 'is_active_confirmed',
                label: 'app.quotations.active_confirmed_title',
                description: 'app.quotations.active_confirmed_description',
                required: false
            },
            {
                type: 'checkbox',
                name: 'is_active_residual',
                label: 'app.quotations.active_residual_title',
                description: 'app.quotations.active_residual_description',
                required: false
            },
            {
                type: 'checkbox',
                name: 'without_product_image',
                label: 'app.quotations.without_product_image',
                description: 'app.quotations.without_product_image_description',
                required: false
            }
        ],
        configXlsParams: [],
        fields: [],
    }), []);

    const handleOpenReportPanel = useCallback(() => {
        if (!canGenerateDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        setOpenReportGenerate(true);
    }, [canGenerateDocument, t]);

    const handlePrintClick = useCallback(async () => {
        if (!canGenerateDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!isPackagingConfirmed) {
            setPendingStatus(null);
            const blockedByPackaging = await requestPackagingConfirmation({nextAction: "doc"});
            if (blockedByPackaging) {
                return;
            }
        }
        handleOpenReportPanel();
    }, [canGenerateDocument, handleOpenReportPanel, isPackagingConfirmed, requestPackagingConfirmation, t]);

    const handleConfirmPackaging = useCallback(() => {
        const nextAction = openType;
        const hasPendingStatus = Boolean(pendingStatus);
        handleToggleConfirmPackaging(false, {preservePendingStatus: hasPendingStatus});
        if (nextAction === "doc") {
            handleOpenReportPanel();
            return;
        }

        if (hasPendingStatus) {
            if (normalizeStatusValue(pendingStatus) === NORMALIZED_CONFIRMED_STATUS && canManageQuantityConfirmation) {
                setConfirmQuantityOpen(true);
                return;
            }
            handleStatusChange(pendingStatus);
            setPendingStatus(null);
        }
    }, [canManageQuantityConfirmation, handleOpenReportPanel, handleStatusChange, handleToggleConfirmPackaging, openType, pendingStatus]);

    const handleValidateDocument = useCallback(async () => {
        if (!canEditDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!companyId || !resolvedDocumentId) {
            toast.error(t("app.documents.body_validation_missing_params", {defaultValue: "Parametri mancanti per la verifica"}));
            return;
        }

        setIsValidatingBody(true);
        try {
            const response = await fetchDocumentBody({
                companyId,
                documentId: resolvedDocumentId,
            });

            const items = resolveBodyItems(response);
            const rowsToValidate = items.reduce((accumulator, item, originalIndex) => {
                if (!item) {
                    return accumulator;
                }

                accumulator.push({item, originalIndex});
                return accumulator;
            }, []);

            if (!rowsToValidate.length) {
                setValidationSummary([]);
                setValidationModalOpen(true);
                return;
            }

            const summary = rowsToValidate.map(({item, originalIndex}) => {
                const packaging = Array.isArray(item?.product?.product_dimensions)
                    ? item.product.product_dimensions.find((dimension) => dimension?.default_dimension)
                    : null;
                const productLabel = item?.name
                    ?? item?.product?.name
                    ?? `${t("app.common.row", {defaultValue: "Riga"})} ${originalIndex + 1}`;
                const quantity = Number(item?.quantity ?? 0);
                const packagingQty = Number(item?.qty_per_packaging ?? packaging?.qty_per_packaging ?? 0);
                const unitPrice = Number(item?.unit_price ?? item?.product?.unit_price ?? 0);
                const netPrice = Number(item?.net_price ?? item?.discount_price ?? item?.product?.net_price ?? unitPrice);
                const totalNetPrice = Number(item?.total_net_price ?? item?.total_price ?? (quantity * netPrice));
                const rowKey = item?.id ?? item?.row_id ?? `row-${originalIndex}`;
                const issues = [];

                if (packagingQty <= 0) {
                    issues.push(t("app.documents.body_validation_missing_packaging", {
                        defaultValue: `Imposta una confezione valida per "${productLabel}".`,
                        product: productLabel,
                    }));
                } else if (quantity > 0 && quantity % packagingQty !== 0) {
                    issues.push(t("app.documents.body_validation_packaging_mismatch", {
                        defaultValue: `La quantità di "${productLabel}" non è multiplo della confezione (${packagingQty}).`,
                        product: productLabel,
                        packaging: packagingQty,
                    }));
                }

                const dimensionChecks = [
                    {key: "length", label: t("app.documents.dimension_length", {defaultValue: "Lunghezza"})},
                    {key: "width", label: t("app.documents.dimension_width", {defaultValue: "Larghezza"})},
                    {key: "height", label: t("app.documents.dimension_height", {defaultValue: "Altezza"})},
                ];

                dimensionChecks.forEach(({key, label}) => {
                    const value = Number(item?.[key] ?? packaging?.[key] ?? 0);
                    if (value <= 0) {
                        issues.push(t("app.documents.body_validation_missing_dimension", {
                            defaultValue: `${label} mancante per "${productLabel}".`,
                            product: productLabel,
                            field: label,
                        }));
                    }
                });

                const grossWeight = Number(item?.gross_weight ?? packaging?.gross_weight ?? 0);
                const netWeightValue = Number(item?.net_weight ?? packaging?.net_weight ?? 0);
                if (grossWeight <= 0 || netWeightValue <= 0) {
                    issues.push(t("app.documents.body_validation_missing_weight", {
                        defaultValue: `Peso non impostato per "${productLabel}".`,
                        product: productLabel,
                    }));
                }
                if (!Number.isFinite(quantity) || quantity <= 0) {
                    issues.push(t("app.documents.body_validation_invalid_quantity", {
                        defaultValue: `Quantità non valida per "${productLabel}".`,
                        product: productLabel,
                    }));
                }

                if (!Number.isFinite(unitPrice)) {
                    issues.push(t("app.documents.body_validation_invalid_unit_price", {
                        defaultValue: `Il prezzo unitario di "${productLabel}" non è valido.`,
                        product: productLabel,
                    }));
                }

                if (quantity > 0 && !Number.isFinite(netPrice)) {
                    issues.push(t("app.documents.body_validation_invalid_net_price", {
                        defaultValue: `Il prezzo netto di "${productLabel}" non è valido.`,
                        product: productLabel,
                    }));
                }

                if (quantity > 0 && !Number.isFinite(totalNetPrice)) {
                    issues.push(t("app.documents.body_validation_invalid_total", {
                        defaultValue: `Il totale di "${productLabel}" non è valido.`,
                        product: productLabel,
                    }));
                }

                if (!item?.product_reference && !item?.product?.product_reference) {
                    issues.push(t("app.documents.body_validation_missing_reference", {
                        defaultValue: `Manca il riferimento prodotto per "${productLabel}".`,
                        product: productLabel,
                    }));
                }

                if (!item?.name && !item?.product?.name) {
                    issues.push(t("app.documents.body_validation_missing_name", {
                        defaultValue: `Manca il nome del prodotto per la riga ${originalIndex + 1}.`,
                        index: originalIndex + 1,
                    }));
                }

                return {
                    rowKey,
                    label: productLabel,
                    issues,
                };
            });

            setValidationSummary(summary);
            setValidationModalOpen(true);
        } catch (error) {
            const message = error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"});
            toast.error(message);
        } finally {
            setIsValidatingBody(false);
        }
    }, [canEditDocument, companyId, resolveBodyItems, resolvedDocumentId, t]);

    return (
        <BasePermissionWrapper
            requiredPermissions={[getPermission(documentPermissionGroup, permissionType.VIEW)]}
            renderBaseOnFail={true}
        >
            {isLoading ? (
                <div className="flex justify-center pt-10">
                    <Loader />
                </div>
            ) : !document ? (
                <div className="pt-10 text-center text-gray-500">
                    {t("empty_state.no_content", {defaultValue: "Nessun contenuto disponibile"})}
                </div>
            ) : (
                <div className="flex min-h-screen flex-col bg-white">

            {resolvedDocumentId && (
                <div className="fixed right-6 top-24 z-40 flex flex-col items-end">
                    <ImportProgress
                        onNeedRefresh={handleImportNeedRefresh}
                        lastImport={latestImport}
                        importType={DOCUMENT_BODY_IMPORT_TYPE}
                        importEntityId={resolvedDocumentId}
                        extraClass="w-72 rounded-md border border-emerald-200 bg-white p-4 shadow-lg"
                    />
                </div>
            )}

            <header
                ref={headerRef}
                className={`md:sticky md:top-0 ${DOCUMENT_HEADER_Z_INDEX_CLASS} border-b border-gray-200 bg-white/90 backdrop-blur`}
            >
                <div className="mx-auto flex  flex-col gap-6 px-4 py-4  lg:flex-row lg:items-center lg:justify-between ">

                    <div className={"flex items-center relative page-title "}>
                        <div className='mr-2'>
                            <SystemTasks
                                taskContext={type}
                                taskContextId={document?.id ?? null}
                                containerClass={"mr-0"}
                                panelZIndexClass={DOCUMENT_SYSTEM_TASKS_PANEL_Z_INDEX_CLASS}
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <div>
                                <p className="text-sm font-light text-gray-500">
                                    {document?.master_document_type?.name ??
                                        t("app.documents.document_label", {defaultValue: "Documento"})}
                                </p>
                                <h1 className="-mt-1 text-2xl font-semibold tracking-tight text-gray-900">
                                    #{document?.internal_reference ?? resolvedDocumentId}
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 -mt-2">
                                <span>
                                    {t("document.form.document_date", {defaultValue: "Data documento"})}
                                    <span className="ml-1 font-medium text-gray-700">{formattedDocumentDate}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                            <div className="flex w-full justify-end sm:w-auto sm:justify-start">
                                <DocumentRequestEventsButton
                                    companyId={companyId}
                                    documentId={resolvedDocumentId}
                                    onSelectEvent={handleRequestEventSelection}
                                    refreshKey={requestEventsRefreshKey}
                                />
                            </div>
                            <div className={`w-full sm:w-24 ${(!canEditDocument || isAcquisitionUpdating) ? "opacity-60 pointer-events-none" : ""}`}>
                                <AcquisitionList
                                    key={document?.acquisition_status ?? "low"}
                                    status={document?.acquisition_status ?? "low"}
                                    onChangeStatus={handleAcquisitionStatusChange}
                                />
                            </div>

                            <div className="w-full sm:w-40">
                                <StatusList
                                    isLoading={isStatusUpdating}
                                    status={displayedStatus}
                                    isEditable={canEditStatus}
                                    statuses={statusOptions}
                                    onChangeStatus={handleStatusSelection}
                                />
                            </div>
                            <div className="w-full sm:w-40 md:hidden">
                                <button
                                    type="button"
                                    onClick={handleToggleMobileDocumentHeader}
                                    className="flex items-center   w-full rounded-md border border-gray-200 bg-white px-2 py-3 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 "
                                    aria-expanded={isMobileDocumentHeaderOpen}
                                >
                                    <InformationCircleIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
                                    {t("document.form.document_header_details", {defaultValue: "Dettagli documento"})}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {!isMdViewport && (
                <Transition show={isMobileDocumentHeaderOpen} as={Fragment}>
                    <Dialog
                        as="div"
                        className="md:hidden"
                        onClose={handleCloseMobileDocumentHeader}
                    >
                        <div className="fixed inset-0 z-[110]">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <Dialog.Overlay className="fixed inset-0 bg-gray-900/50" />
                            </Transition.Child>

                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="translate-y-full"
                                enterTo="translate-y-0"
                                leave="ease-in duration-150"
                                leaveFrom="translate-y-0"
                                leaveTo="translate-y-full"
                            >
                                <div
                                    className="fixed inset-x-0 bottom-0 flex flex-col rounded-t-2xl bg-white shadow-xl"
                                    style={{top: documentHeaderHeight ?? 0}}
                                >
                                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                                        <Dialog.Title className="text-sm font-semibold text-gray-900">
                                            {t("document.form.document_header_details", {defaultValue: "Dettagli documento"})}
                                        </Dialog.Title>
                                        <button
                                            type="button"
                                            onClick={handleCloseMobileDocumentHeader}
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white"
                                        >
                                            <XIcon className="h-4 w-4" aria-hidden="true" />
                                            <span className="sr-only">
                                                {t("app.common.close", {defaultValue: "Chiudi"})}
                                            </span>
                                        </button>
                                    </div>
                                    <div className="flex-1 bg-slate-50 px-2 pb-4">
                                        <DocumentHeader
                                            document={document}
                                            onEdit={canEditDocument ? handleEdit : undefined}
                                            onUpdate={handlePartialUpdate}
                                            companyId={companyId}
                                            canEdit={canEditDocument}
                                            canEditConnectionVisibility={canEditConnectionVisibility}
                                        />
                                    </div>
                                </div>
                            </Transition.Child>
                        </div>
                    </Dialog>
                </Transition>
            )}

            <div className="md:flex-1 md:overflow-hidden">
                <div className="h-screen  md:h-[calc(100svh-151px)] md:pb-0 pb-[220px]   overflow-y-auto bg-slate-50">
                    {isMdViewport && (
                        <DocumentHeader
                            document={document}
                            onEdit={canEditDocument ? handleEdit : undefined}
                            onUpdate={handlePartialUpdate}
                            companyId={companyId}
                            canEdit={canEditDocument}
                            canEditConnectionVisibility={canEditConnectionVisibility}
                        />
                    )}

                    <main>
                        <section ref={documentBodySectionRef}>
                            <DocumentBody
                                document={document}
                                companyId={companyId}
                                documentId={resolvedDocumentId}
                                documentType={type}
                                isLoading={isLoading}
                                importRefreshKey={importRefreshKey}
                                onImportQueued={handleImportQueued}
                                showMargin={canViewMarginData && showMargin}
                                onDocumentTotalsRefresh={handleRefreshDocumentDetail}
                                canEdit={canEditDocument}
                                enableStickyToolbar={!isMdViewport}
                                stickyToolbarOffset={resolvedToolbarOffset}
                                externalConfirmedQuantityRequest={externalConfirmedQuantityRequest}
                                onConfirmedQuantityEventsChange={handleConfirmedQuantityEventsUpdated}
                            />
                        </section>
                    </main>
                </div>
            </div>

            <footer className="md:sticky fixed inset-x-0 bottom-0  z-[50] border-t border-gray-900/50 bg-gray-900">
                <div className="mx-auto flex flex-col gap-4 px-4 py-3 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
                    <div className="order-2 flex w-full flex-wrap items-center gap-3 sm:order-1 sm:w-auto sm:flex-nowrap">
                        <div className="flex">
                            <button
                                type="button"
                                onClick={handleNavigateBack}
                                className="h-8 rounded-md border border-white/30 px-3 text-sm font-medium text-white hover:bg-white/10"
                            >
                                {t("app.common.back", {defaultValue: "Indietro"})}
                            </button>
                        </div>
                        <div className="flex gap-2">
                            {canGenerateDocument && (
                                <button
                                    type="button"
                                    onClick={handlePrintClick}
                                    className="flex h-8 w-8 items-center justify-center rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                    aria-label={t("app.documents.actions.print", {defaultValue: "Stampa o Anteprima"})}
                                    title={t("app.documents.actions.print", {defaultValue: "Stampa o Anteprima"})}
                                >
                                    <PrinterIcon className="h-5 w-5" aria-hidden="true" />
                                    <span className="sr-only">
                                        {t("app.documents.actions.print", {defaultValue: "Stampa o Anteprima"})}
                                    </span>
                                </button>
                            )}
                            {canEditDocument && (
                                <button
                                    type="button"
                                    onClick={handleValidateDocument}
                                    disabled={isValidatingBody}
                                    aria-busy={isValidatingBody}
                                    className={`flex h-8 w-8 items-center justify-center rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900 ${isValidatingBody ? "cursor-progress opacity-70" : ""}`}
                                    aria-label={t("app.documents.actions.validate_document", {defaultValue: "Verifica documento"})}
                                    title={t("app.documents.actions.validate_document", {defaultValue: "Verifica documento"})}
                                >
                                    <BadgeCheckIcon className="h-5 w-5" aria-hidden="true" />
                                    <span className="sr-only">
                                        {t("app.documents.actions.validate_document", {defaultValue: "Verifica documento"})}
                                    </span>
                                </button>
                            )}
                            {hasConfirmationInfo && (
                                <Popover className="relative">
                                    <Popover.Button
                                        className="group inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white shadow-sm transition hover:border-white/40 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
                                        aria-label={t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                        title={t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                    >
                                        <SignatureIcon className={`h-5 w-5 transition duration-150 group-hover:scale-105 ${confirmationIconColor}`} aria-hidden="true" />
                                        <span className="sr-only">
                                            {t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                        </span>
                                    </Popover.Button>
                                    <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-150"
                                        enterFrom="opacity-0 translate-y-1"
                                        enterTo="opacity-100 translate-y-0"
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100 translate-y-0"
                                        leaveTo="opacity-0 translate-y-1"
                                    >
                                        <Popover.Panel className="fixed inset-x-4 bottom-[4.5rem] z-30 w-auto max-w-full transform origin-bottom rounded-2xl sm:absolute sm:bottom-full sm:left-0 sm:mb-4 sm:w-[26rem] sm:max-w-[calc(100vw-2rem)] sm:origin-bottom-left">
                                            <div className="rounded-2xl border border-gray-200 bg-white/95 p-4 text-gray-900 shadow-2xl ring-1 ring-black/5 backdrop-blur sm:p-5">
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                            {t("app.documents.actions.confirmation_details", {defaultValue: "Dettagli conferma"})}
                                                        </p>

                                                    </div>
                                                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${documentSignatureBadgeClass}`}>
                                                        <span className="inline-block h-2 w-2 rounded-full bg-current" />
                                                        {documentSignatureBadge}
                                                    </span>
                                                </div>
                                                {
                                                    documentSignatureCards.length > 0 && (
                                                        <div className="mt-4 max-h-60 space-y-3 overflow-y-auto text-left sm:max-h-80">
                                                            {documentSignatureCards}
                                                        </div>
                                                    )
                                                }

                                            </div>
                                        </Popover.Panel>
                                    </Transition>
                                </Popover>
                            )}
                            <Menu as="div" className="relative inline-block text-left">
                                <Menu.Button
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                    aria-label={t("app.documents.actions.more", {defaultValue: "Altro"})}
                                >
                                    <DotsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
                                </Menu.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items className="absolute bottom-full right-0 z-20 mb-2 w-48 origin-bottom-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        {canViewMarginData && (
                                            <Menu.Item>
                                                {({active}) => (
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowMargin((previous) => !previous)}
                                                        className={`${active ? "bg-gray-100 text-gray-900" : "text-gray-700"} flex w-full items-center px-4 py-2 text-sm`}
                                                    >
                                                        {showMargin
                                                            ? t("app.documents.actions.hide_margin", {defaultValue: "Nascondi margine"})
                                                            : t("app.documents.actions.show_margin", {defaultValue: "Mostra margine"})}
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        )}
                                        {canDeleteDocument && (
                                            <Menu.Item>
                                                {({active}) => (
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsRemoveOpen(true)}
                                                        className={`${active ? "bg-gray-100 text-gray-900" : "text-gray-700"} flex w-full items-center px-4 py-2 text-sm`}
                                                    >
                                                        {t("app.common.delete", {defaultValue: "Elimina"})}
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        )}
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        </div>
                    </div>
                    <div className="order-1 flex flex-col gap-2 text-left sm:order-2 sm:items-end sm:text-right" ref={totalsRef}>
                        <dl className="relative space-y-3 rounded-lg bg-gray-800/70 p-3 sm:space-y-3 sm:rounded-none sm:bg-transparent sm:p-0" aria-busy={isRefreshingTotals}>
                            <div className={`flex flex-wrap items-center justify-start gap-3 text-white sm:justify-end ${isRefreshingTotals ? "animate-pulse" : ""}`}>
                                <dt className="w-full text-xs font-semibold uppercase tracking-wide text-gray-300 sm:w-auto sm:text-sm sm:normal-case sm:tracking-normal">
                                    {t("app.documents.total", {defaultValue: "Totale"})}
                                </dt>
                                <dd className="text-2xl font-semibold text-white">{formattedTotal}</dd>
                                {/*{showOriginalTotal && formattedOriginalTotal && (*/}
                                {/*    <div className="text-sm font-semibold text-white/60 line-through">*/}
                                {/*        {formattedOriginalTotal}*/}
                                {/*    </div>*/}
                                {/*)}*/}
                                {hasPriceRange && canViewMarginData && (
                                    <div className="mt-1 flex items-center -ml-4">
                                        <PriceRange
                                            symbol={currencySymbol ?? ""}
                                            purchasePrice={normalizedPurchasePrice || false}
                                            price={normalizedNetPrice}
                                            isLeft
                                            showMarginTotals={false}
                                            showMargin={showMargin}
                                        />
                                    </div>
                                )}
                                {canEditDocument && (
                                    <div className="flex items-center gap-1">
                                        {activeDiscountPercentLabel && (
                                            <span className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-xs font-semibold text-emerald-200 ring-1 ring-emerald-400/40">
                                                {activeDiscountPercentLabel}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleOpenDiscountPopup}
                                            className="rounded-full p-1 text-emerald-200 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                            aria-label={t("app.documents.manage_discount", {defaultValue: "Gestisci sconto documento"})}
                                        >
                                            <TagIcon className="h-4 w-4" aria-hidden="true"/>
                                        </button>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDocumentTotalsVisible(false);
                                        setTotalsVisible((prev) => !prev);
                                    }}
                                    className="rounded-full p-1 text-gray-300 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                    aria-label={t("app.documents.view_total_details", {defaultValue: "Mostra dettaglio totale"})}
                                    aria-expanded={areTotalsVisible}
                                >
                                    <InformationCircleIcon className="h-4 w-4" aria-hidden="true"/>
                                </button>

                                {hasDocumentTotalsData && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTotalsVisible(false);
                                            setDocumentTotalsVisible((prev) => !prev);
                                        }}
                                        className="rounded-full p-1 text-gray-300 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-gray-900"
                                        aria-label={t("app.documents.view_document_totals", {defaultValue: "Mostra dettagli volume"})}
                                        aria-expanded={areDocumentTotalsVisible}
                                    >
                                        <CubeIcon className="h-4 w-4" aria-hidden="true"/>
                                    </button>
                                )}
                            </div>
                            {hasDocumentTotalsData && areDocumentTotalsVisible && (
                                <div
                                    className={`absolute bottom-12 right-0 z-20 w-72 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-lg ${isRefreshingTotals ? "animate-pulse" : ""}`}
                                >
                                    {hasDocumentTotalsTabs && (
                                        <div
                                            className="mb-3 flex items-center gap-1 rounded-md bg-gray-100 p-1 text-xs font-medium text-gray-600">
                                            {availableDocumentTotalsSections.map((section) => {
                                                const isActiveSection = section.id === selectedTotalsSection;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={`document-totals-${section.id}`}
                                                        onClick={() => setSelectedTotalsSection(section.id)}
                                                        className={`flex-1 rounded-md px-2 py-1 transition ${isActiveSection ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                                                        aria-pressed={isActiveSection}
                                                    >
                                                        {section.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <dl className="space-y-2 text-sm" aria-busy={isRefreshingTotals}>
                                        {documentTotalsBreakdown.map((item) => {
                                            const Icon = item.icon ?? null;
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between gap-4"
                                                >
                                                    <dt className="flex items-center gap-1 font-medium text-gray-600">
                                                        {Icon ? (
                                                            <Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                                                        ) : null}
                                                        <span>{item.label}</span>
                                                    </dt>
                                                    <dd className="text-right">
                                                        <span className={`font-semibold ${item.warning ? "text-amber-600" : "text-gray-900"}`}>
                                                            {item.value}
                                                        </span>
                                                        {item.warning ? (
                                                            <span className="mt-1 block text-xs font-medium text-amber-600">
                                                                {item.warning}
                                                            </span>
                                                        ) : null}
                                                    </dd>
                                                </div>
                                            );
                                        })}
                                    </dl>
                                </div>
                            )}
                            {areTotalsVisible && (
                                <div
                                    className={`absolute bottom-12 right-0 z-20 w-64 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-lg ${isRefreshingTotals ? "animate-pulse" : ""}`}
                                >
                                    {hasTotalsTabs && (
                                        <div className="mb-3 flex items-center gap-1 rounded-md bg-gray-100 p-1 text-xs font-medium text-gray-600">
                                            {availableTotalsSections.map((section) => {
                                                const isActiveSection = section.id === selectedTotalsSection;
                                                return (
                                                    <button
                                                        type="button"
                                                        key={section.id}
                                                        onClick={() => setSelectedTotalsSection(section.id)}
                                                        className={`flex-1 rounded-md px-2 py-1 transition ${isActiveSection ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                                                        aria-pressed={isActiveSection}
                                                    >
                                                        {section.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <dl className="space-y-2 text-sm" aria-busy={isRefreshingTotals}>
                                        {totalsBreakdown.map((item) => (
                                            <div
                                                key={item.id}
                                                className="flex items-center justify-between gap-4"
                                            >
                                                <dt className={item.isHighlight ? "font-semibold text-gray-900" : "font-medium text-gray-600"}>
                                                    {item.label}
                                                </dt>
                                                <dd className={item.isHighlight ? "font-semibold text-gray-900" : "font-medium text-gray-700"}>
                                                    {item.value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>
            </footer>

            {canEditDocument && (
                <FormPopup
                    title={t("app.documents.discount_modal.title", {defaultValue: "Gestisci sconto documento"})}
                    isOpen={isDiscountPopupOpen}
                    setIsOpen={setIsDiscountPopupOpen}
                    defaultWith="max-w-xl"
                >
                    <div className="space-y-5">
                        <p className="text-sm text-gray-500">
                            {t("app.documents.discount_modal.description", {
                                defaultValue: "Applica o aggiorna lo sconto complessivo e visualizza subito il nuovo totale.",
                            })}
                        </p>

                        <div>
                            <p className="text-sm font-semibold text-gray-700">
                                {t("app.documents.discount_modal.mode_label", {defaultValue: "Metodo di inserimento"})}
                            </p>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setDiscountMode("percent")}
                                    className={`rounded-xl border px-4 py-3 text-left transition ${discountMode === "percent" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                                >
                                    <p className="text-sm font-semibold">
                                        {t("app.documents.discount_modal.mode_percent", {defaultValue: "Percentuale"})}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {t("app.documents.discount_modal.mode_percent_hint", {defaultValue: "Inserisci lo sconto in %."})}
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => canUseDiscountValueMode && setDiscountMode("value")}
                                    disabled={!canUseDiscountValueMode}
                                    className={`rounded-xl border px-4 py-3 text-left transition ${discountMode === "value" ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"} ${!canUseDiscountValueMode ? "cursor-not-allowed opacity-60" : ""}`}
                                >
                                    <p className="text-sm font-semibold">
                                        {t("app.documents.discount_modal.mode_value", {defaultValue: "Valore"})}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {t("app.documents.discount_modal.mode_value_hint", {defaultValue: "Inserisci lo sconto in valuta."})}
                                    </p>
                                </button>
                            </div>
                            {!canUseDiscountValueMode && (
                                <p className="mt-2 text-xs text-amber-600">
                                    {t("app.documents.discount_modal.mode_value_disabled", {defaultValue: "Calcolo in valuta disponibile solo con un totale valido."})}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-sm font-semibold text-gray-700">
                                {discountMode === "percent"
                                    ? t("app.documents.discount_modal.percent_label", {defaultValue: "Percentuale di sconto"})
                                    : t("app.documents.discount_modal.value_label", {defaultValue: "Valore dello sconto"})
                                }
                            </label>
                            <div className="mt-2 relative">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={discountMode === "percent" ? discountPercentInput : discountValueInput}
                                    onChange={(event) => {
                                        if (discountMode === "percent") {
                                            setDiscountPercentInput(event.target.value);
                                        } else {
                                            setDiscountValueInput(event.target.value);
                                        }
                                    }}
                                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-base font-semibold text-gray-800 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                    placeholder={discountMode === "percent" ? "0,00" : "0.00"}
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm font-semibold text-gray-400">
                                    {discountMode === "percent" ? "%" : (currencySymbol || currencyCode || "")}
                                </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                {discountMode === "percent"
                                    ? t("app.documents.discount_modal.percent_hint", {defaultValue: "Valore compreso tra 0% e 100%."})
                                    : t("app.documents.discount_modal.value_hint", {defaultValue: "Il valore verrà convertito automaticamente in percentuale."})
                                }
                            </p>
                            {discountMode === "value" && (
                                <p className="mt-1 text-xs text-emerald-600">
                                    {resolvedPercentLabel
                                        ? t("app.documents.discount_modal.value_conversion", {
                                            defaultValue: "Equivale a {{value}} di sconto totale.",
                                            value: resolvedPercentLabel,
                                        })
                                        : t("app.documents.discount_modal.value_conversion_pending", {
                                            defaultValue: "Inserisci un importo valido per calcolare la percentuale.",
                                        })}
                                </p>
                            )}
                        </div>

                        <div className="rounded-2xl border border-gray-100 bg-white/70 p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {t("app.documents.discount_modal.current_total", {defaultValue: "Totale attuale"})}
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-gray-900">{formattedTotal}</p>
                                    {showOriginalTotal && formattedOriginalTotal && (
                                        <p className="text-xs font-medium text-gray-400 line-through">
                                            {formattedOriginalTotal}
                                        </p>
                                    )}
                                </div>
                                {resolvedPercentLabel && (
                                    <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600 ring-1 ring-emerald-500/30">
                                        {resolvedPercentLabel}
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                <div className="rounded-xl bg-emerald-50/70 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800/70">
                                        {t("app.documents.discount_modal.discount_value", {defaultValue: "Sconto"})}
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-emerald-700">
                                        {previewDiscountFormatted}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-gray-900/5 p-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                        {t("app.documents.discount_modal.new_total", {defaultValue: "Nuovo totale"})}
                                    </p>
                                    <p className="mt-1 text-lg font-semibold text-gray-900">
                                        {previewTotalFormatted}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <button
                                type="button"
                                onClick={handleDiscountReset}
                                className="text-sm font-semibold text-gray-500 hover:text-gray-700"
                            >
                                {t("app.documents.discount_modal.reset_action", {defaultValue: "Azzera sconto"})}
                            </button>
                            <div className="flex flex-1 items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsDiscountPopupOpen(false)}
                                    className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                                >
                                    {t("app.common.close", {defaultValue: "Chiudi"})}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDiscountSave}
                                    disabled={isDiscountActionDisabled}
                                    className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white transition ${
                                        isDiscountActionDisabled
                                            ? "cursor-not-allowed bg-emerald-400/50"
                                            : "bg-emerald-500 hover:bg-emerald-600"
                                    }`}
                                >
                                    {isDiscountUpdating
                                        ? t("app.common.loading", {defaultValue: "Caricamento"})
                                        : t("app.documents.discount_modal.save", {defaultValue: "Applica sconto"})}
                                </button>
                            </div>
                        </div>
                    </div>
                </FormPopup>
            )}

            {canGenerateDocument && (
                <SingleReport
                    isActiveForm={Boolean(isExporter)}
                    isOpen={isOpenReportGenerate}
                    setIsOpen={(nextOpen) => {
                        setOpenReportGenerate(nextOpen);
                    }}
                    isSpecific
                    isDynamic
                    isPersonalized
                    filename={document?.internal_reference ?? false}
                    reportDetails={documentReportDetails}
                    entityIdOverride={resolvedDocumentId ?? undefined}
                    panelZIndexClass="z-[90]"
                    overlayZIndexClass="z-[0]"
                />
            )}

            <ValidationSummaryModal
                isOpen={isValidationModalOpen}
                onClose={() => setValidationModalOpen(false)}
                summary={validationSummary}
                t={t}
            />

            {/* Cloning progress popup */}
            {isCloningPopupOpen && (
                <FormPopup
                    title={t("app.documents.cloning.title")}
                    isOpen={isCloningPopupOpen}
                    setIsOpen={setIsCloningPopupOpen}
                    isActiveCloseBack={true}
                    defaultWith="max-w-md"
                >
                    <div className="flex flex-col items-center justify-center gap-4 py-2">
                        <Loader />
                        <div className="text-center text-sm text-gray-700">
                            <p className="mb-1">{t("app.documents.cloning.message")}</p>
                            <p className="text-xs text-gray-500">{t("app.documents.cloning.autoclose")}</p>
                        </div>
                    </div>
                </FormPopup>
            )}

            {isStatusConfirmCheckOpen && statusConfirmDetail && (
                <ConfirmPopup
                    isOpen={isStatusConfirmCheckOpen}
                    setIsOpen={handleCloseStatusConfirmCheck}
                    detail={statusConfirmDetail}
                    isJustConfirm
                    onConfirmed={handleConfirmStatusFromCheck}
                />
            )}

            {isOpenRequird && (
                <RequiredPopup
                    title={t("app.quotations.required_error_title")}
                    description={t("app.quotations.required_error_description")}
                    isOpen={isOpenRequird}
                    setIsOpen={() => {
                        setOpenRequird(false);
                        setRequiredDetail([]);
                    }}
                    requireds={requiredDetail}
                />
            )}

            {isConfirmQuantityOpen && (
                <ConfirmTextPopup
                    isOpen={isConfirmQuantityOpen}
                    setIsOpen={handleToggleConfirmQuantity}
                    cancelTitle={t("app.quotations.no_confirm")}
                    detail={{
                        title: t("app.quotations.quantity_status_title"),
                        message: t("app.quotations.quantity_status_description"),
                    }}
                    onConfirmed={handleConfirmQuantityStatus}
                    onCancel={handleSkipQuantityStatus}
                />
            )}

            {isOpenConfirmPackaging && (
                <ConfirmTextPopup
                    zIndex={"z-[60]"}
                    isOpen={isOpenConfirmPackaging}
                    setIsOpen={handleToggleConfirmPackaging}
                    detail={{
                        title: t("app.quotations.packing_title"),
                        message: t("app.quotations.packing_description"),
                    }}
                    onConfirmed={handleConfirmPackaging}
                />
            )}

            {canDeleteDocument && removeDetail && (
                <RemovePopup
                    isOpen={isRemoveOpen}
                    setIsOpen={setIsRemoveOpen}
                    detail={removeDetail}
                    onRemoveConfirmed={() => {
                        setIsRemoveOpen(false);
                        if (slug) {
                            navigate(buildListPath(slug), {replace: true});
                        }
                    }}
                />
            )}
                </div>
            )}
        </BasePermissionWrapper>
    );
};

export default DocumentDetailPage;
